// Smarttix design tokens — single source of color truth for the FE.
// Per feedback_smarttix_navy_palette.md:
//   - Brand palette = chrome/headers/identity
//   - Category palette = per-K rendering only (don't substitute one for the other)
// Per feedback_orange_k4_exclusive.md:
//   - #BC5A00 = K4 only (AA-darkened from #DC6A00)
//   - #B91C1C = Bearbeitungsfehler / system error

const SLATE_TINT = '#e6e7ec';
const SLATE_INK = '#3d4152';

export const theme = {
  colors: {
    // ── Brand navy (chrome, headers, accents) ──
    brand: {
      primary: '#000d66',
      dark: '#131740',
      darkSecondary: '#0a1253',
      mid: '#394388',
      light: '#555e99',
      muted: '#666e9c',           // WCAG 1.4.3 — 4.91:1 on white (was #7179aa, 4.19:1 — failed AA at 11px normal)
    },
    // Aliases used by older code paths (kept until B3 sweep completes; remove after Task 16).
    navy: '#000d66',
    navyDark: '#131740',          // was #1c2877 — aligned to smarttix.web globals.css
    navyMid: '#394388',
    navyLight: '#555e99',
    navyMuted: '#666e9c',         // WCAG 1.4.3 — 4.91:1 on white (alias for brand.muted)

    // ── Demo-only accent (Simulierte Systemdaten button) ──
    // Deliberately off the navy brand axis so the demo affordance reads as distinct from
    // production actions; also distinct from the escalation purples. Never production UI. Spec 2026-07-08.
    demoAccent: '#6D3BD1',
    demoAccentHover: '#5c31b0',

    // ── Reply-engine hand-off zones (VNB D3, GRÜN/GELB/ROT doctrine — exactly three) ──
    // GELB is deliberately NOT the K4 orange (#BC5A00, reserved) and ROT is deliberately
    // NOT the Bearbeitungsfehler red (#B91C1C): zone = hand-off semantics, not error/danger.
    zone: {
      gruen: { fg: '#1D6A3A', bg: '#E7F3EC', border: '#BFDECB' },
      gelb: { fg: '#7A5C00', bg: '#FAF3D9', border: '#E5D28A' },
      rot: { fg: '#8F1C2E', bg: '#F9E8EA', border: '#E5B8BF' },
    },

    // ── Mode-color family. Active = navy anchor. rejections/archived have light|dark variants
    // selected by the per-user palette preference (PaletteModeContext). See spec 2026-06-16. ──
    mode: {
      active: {
        card: '#000D66', tile: '#E8EBF4', fill: null as string | null, tileText: '#000D66',
        chrome: '#FFFFFF', bigText: '#FFFFFF', pillOnBg: '#E8EBF4', pillOnText: '#000D66',
        pillOffBorder: 'rgba(255,255,255,0.45)', // was 0.35 → 2.92:1; 0.45 → 4.08:1 (WCAG 1.4.11 ✓)
      },
      rejections: {
        light: { card: '#8B91B9', tile: '#EBEBF2', fill: '#B9BDD5', tileText: '#565E8F', chrome: '#000D66', bigText: '#FFFFFF', pillOnBg: '#565E8F', pillOnText: '#FFFFFF', pillOffBorder: 'rgba(0,13,102,0.65)' }, // was 0.4 → 2.04:1; 0.65 → 3.29:1 ✓
        dark:  { card: '#515982', tile: '#E8EBF4', fill: '#B9BDD5', tileText: '#5C6383', chrome: '#FFFFFF', bigText: '#FFFFFF', pillOnBg: '#E8EBF4', pillOnText: '#515982', pillOffBorder: 'rgba(255,255,255,0.5)' },
      },
      archived: {
        light: { card: '#BEBEC9', text: '#000D66' },
        dark:  { card: '#57555C', text: '#FFFFFF' },
      },
    },

    // ── Semantic ──
    accent: '#000d66',
    textSecondary: '#5C617F',     // WCAG 1.4.3 — 5.97:1 on white; FAILS on navy bg, use textOnDark there
    textOnDark: '#a8acca',        // WCAG 1.4.3 — 7.54:1 on navy #000d66, 7.69:1 on navyDark #131740
    borderLight: '#c6c9dd',
    bgLight: '#e3e4ee',
    bgPage: '#f5f6fa',
    white: '#ffffff',

    focusRing: '#000d66',         // WCAG 2.4.7 — visible focus indicator; brand navy, 16.81:1 on white
    success: '#1d6a3a',           // green for positive deltas (PDF/FE convention)
    error: '#B91C1C',             // Bearbeitungsfehler red — aligned to PDF lock (was #dc2626)
    warning: '#BC5A00',           // K4 orange — aligned to AA-darkened K4 (#DC6A00→ now #BC5A00; orig #d97706); never use as a generic warning
    suggestion: '#8A5A00',        // suggestion/proposal amber — NOT the K4 orange, which is warning/accentOrange
    deltaNegative: '#88661c',     // muted amber — for "delta worsened" without conflicting with K4/Bearbeitungsfehler
    neutral: '#6b7280',           // Neutral slice in sentiment chart (archive-mode mute now uses mode.archived)
    // Box grounds for the Begründung's two house lines (part 5-2, board 3): a pale wash of the danger
    // hue under the fixed procedure, a pale navy wash under the attack warning. Grounds only — the
    // text on them stays navy/navyMid, never the tint.
    warningTint: '#fff8eb',
    navyTint: '#eef0fa',

    // ⚠ Same hue as the served K4 category colour (#BC5A00). Pre-existing: PII-reveal borders and PanelSection collapse
    // arrows already render this orange; feedback_orange_k4_exclusive reserves it for K4.
    // Flagged to the operator at plan close — this token only NAMES the current state.
    accentOrange: '#BC5A00',
  },
  // Status strip colors — navy palette shades.
  statusStrip: {
    eingereicht: '#394388',
    offen: '#000d66',
    entschieden: '#000044',
    error: '#B91C1C',             // aligned to Bearbeitungsfehler red
    unclassified: '#6B6F7F',      // the unclassified-bucket hue (the served KU/unbestimmt colour), a chrome token here
  },
  // THE MARKET-PARTNER PILL's own token (row grammar R33, 2026-09-19; hue picked by the operator
  // from the board's three candidates). A hue nothing else in this app owns, because the partner is
  // a WHO and every token already here says something else: navy is the app's own chrome, the
  // served handling hues say what the mail is, K4 orange is a category, and the registry's amber
  // says „prüfen".
  // ONE VALUE, and only ink: the pill is ALWAYS OUTLINED (ruling R33b, 2026-09-19) — plum text on
  // white, with a 1.5px plum border whose STYLE carries the registry fact (solid = confirmed or
  // seeded, dashed = a proposal). The `fill` half is retired with that ruling: a filled pill read
  // as a second classification badge and competed with the handling badge beside it for the same
  // glance, which is the same reason the decision badge is outlined rather than filled.
  // Holds WCAG AA as text on white; __tests__/themeContrast.test.ts pins that one direction, which
  // is now the only direction this token is rendered in.
  partnerPill: { ink: '#5B2A86' },
  // part 7, 2026-10-11 (scheme 2): the lifecycle STATE tag's six hues — active tabs navy (Offen solid,
  // Genehmigt navy outline, Uploads pale), archive slate (Liegengeblieben SOLID = the archive's "needs a
  // look", Erledigt quiet tint, Ungeklaert dashed). Keyed by LifecycleBucket. `border: null` = no border;
  // outlined variants sit on white. __tests__/themeContrast.test.ts pins fg-on-bg (outlined: fg-on-white).
  stateTag: {
    uploads:         { bg: '#f3f4f9', fg: '#394388', border: null },
    offen:           { bg: '#000d66', fg: '#ffffff', border: null },
    entschieden:       { bg: '#ffffff', fg: '#000d66', border: '1.5px solid #000d66' },
    erledigt:        { bg: SLATE_TINT, fg: SLATE_INK, border: null },
    liegengeblieben: { bg: SLATE_INK, fg: '#ffffff', border: null },
    ungeklaert:      { bg: '#ffffff', fg: '#3d4152', border: '1.5px dashed #6B6F7F' },
  } as const,
  // part 7 fix loop, 2026-10-13 (operator ruling, option B): the role's UNCLASSIFIED primary value is
  // the quiet slate tint -- the Erledigt language, a non-hue, never a category -- in every primary
  // register (categoryMeta.resolvePrimarySwatch). One source with stateTag.erledigt (same constants).
  // `active*` is its filter bubble when selected: slate fill, white ink, so "selected" still reads as
  // unclassified. __tests__/themeContrast.test.ts pins both ink-on-fill directions.
  unclassifiedSwatch: { fill: SLATE_TINT, ink: SLATE_INK, activeFill: SLATE_INK, activeInk: '#ffffff' } as const,
  // THE row-strip width, keyed on the row layout (operator ruling 2026-09-16): a strip carrying a
  // face needs room for it; a strip carrying nothing is a colour band and reads at half width.
  // Every list that paints the strip reads this — the active list, the ledger and the dashboard's
  // Verlauf (which scales it to its own compact row) — so the two widths cannot drift page by page.
  // Keyed by RowLayout, so a site indexes it with cm.rowLayout and cannot pick the wrong one.
  stripWidth: { strip_abbr: 32, strip_label: 16 } as const,
  shadows: {
    card: '0 1px 3px rgba(0, 0, 0, 0.1)',
    cardHover: '0 2px 8px rgba(0, 0, 0, 0.15)',
    // BentoCard default: navy-tinted soft lift (boundary signal w/o border chrome)
    cardLifted: '0 4px 12px rgba(6, 16, 90, 0.07), 0 1px 3px rgba(6, 16, 90, 0.05)',
    // BentoCard hover: stronger lift — same direction, more intensity
    cardLiftedHover: '0 8px 20px rgba(6, 16, 90, 0.12), 0 2px 4px rgba(6, 16, 90, 0.08)',
  },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 },
  // App chrome geometry an OVERLAY has to reason about (Part C walk round, 2026-09-09). NavBar is
  // `position:sticky` at z-index 1000, above every overlay in the app, so anything that lays itself
  // out against the viewport has to know where the bar's bottom edge is or it opens underneath it —
  // which is exactly what CompareStage did until this token existed. NavBar reads it for its own
  // height too, so the number cannot drift from the bar it describes.
  layout: { navBarHeight: 56 },
  borderRadius: { sm: 4, md: 8, lg: 12 },
  // Named layering token — InfoTooltip is the one component so far that needs an explicit "always on
  // top" z-index; every other overlay in the app (modals, toasts, sticky nav) still hand-rolls its
  // own literal (100-1200 range) rather than sharing a token. tooltip stays above all of them.
  // popover = a layer a control hangs under itself (the filter panel's expanded body, 2026-09-16):
  // above the list it covers, deliberately BELOW the sticky NavBar's 1000 so it can never paint over
  // the app's own chrome, and below tooltip, which stays on top of everything.
  zIndex: { tooltip: 9999, popover: 900 },
  font: { family: "'Poppins', sans-serif" },
};

/**
 * A hue mixed with white in sRGB — `ratio` is the hue's share (0.22 = 22 % hue, 78 % white). THE
 * derivation of every "tint of a served hue" in the app (the decision badge's key ground, small
 * facts on a row 2026-10-19): derived from the served hue at render, never a second stored token
 * that could drift from the hue it tints. A 3- or 6-digit hex comes back as six-digit lowercase hex;
 * any other served colour string (rgb(), a name) is mixed by the browser through `color-mix`, so the
 * tint is never silently the hue itself.
 */
export const tintOf = (hex: string, ratio: number): string => {
  const raw = hex.trim();
  const short = /^#?([0-9a-f]{3})$/i.exec(raw);
  const m = /^#?([0-9a-f]{6})$/i.exec(short ? '#' + short[1].split('').map(c => c + c).join('') : raw);
  if (!m) return `color-mix(in srgb, ${raw} ${Math.round(ratio * 100)}%, #ffffff)`;
  const n = parseInt(m[1], 16);
  const mix = (c: number) => Math.round(c * ratio + 255 * (1 - ratio));
  const r = mix((n >> 16) & 255), g = mix((n >> 8) & 255), b = mix(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};
